import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { Input, Modal } from "antd";

import styles from "./stylesWithdrawalReview.module.css";

import { AGENT_ACTIONS } from "../../../utils/Constants";
import TableTabs from "./TableTabs/TableTabs";
import PendingRequestsTable from "./PendingRequestsTable/PendingRequestsTable";
import ProcessedRequestsTable from "./ProcessedRequestsTable/ProcessedRequestsTable";
import InProgressTable from "./InProgressTable/InProgressTable";
import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";
import CustomSelect from "../../CommonComponents/CustomSelect/CustomSelect";
import ViewDetailsModal from "./ViewDetailsModal/ViewDetailsModal";
import DeclineRequestModal from "./DeclineRequestModal/DeclineRequestModal";
import ApproveRequestModal from "./ApproveRequestModal/ApproveRequestModal";
import RefundRequestModal from "./RefundRequestModal/RefundRequestModal";
import RulesTab from "./RulesTab/RulesTab";
import "../../CommonComponents/ExportToXlsxButton/stylesExportToXlsxButton.css";
import AppContext from "../../../context/AppContext";

import {
  WithdrawalReviewProvider,
  useWithdrawalReview,
} from "./WithdrawalReviewContext";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { canAccessWithdrawalReviewTab } from "../../../utils/constants/permissionsConstants";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedSearchQuery } from "../../../hooks/useDebouncedSearchQuery";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { useProductFeatureFlag } from "../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../utils/products";
import {
  PaymentDetailsDrawer,
  toPaymentDetailsIds,
} from "../../shared/PaymentDetailsDrawer";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

const METHOD_OPTIONS = [
  { value: "bank_ach", label: "Bank ACH" },
  { value: "push_2_card", label: "Push 2 Card" },
  { value: "spei", label: "SPEI" },
];

function WithdrawalReviewContent() {
  usePageTitle(AppStrings.WITHDRAWAL_REVIEW_TITLE);

  const { adminActions, adminPermissions } = useContext(AppContext);
  const currencySymbol = useCurrencySymbol();
  const canReviewWithdrawal = adminActions?.includes(
    AGENT_ACTIONS?.REVIEW_WITHDRAWAL,
  );
  const { isEnabled: isWithdrawalRefundEnabled } =
    useProductFeatureFlag(
      "withdrawal_refund",
      TRIVELTA_PRODUCTS.PAYMENTS,
    );
  const canRefundWithdrawal =
    isWithdrawalRefundEnabled === true &&
    adminActions?.includes(AGENT_ACTIONS?.REFUND_WITHDRAWAL);

  const {
    selectedTab,
    setSelectedTab,
    rangeDates,
    setRangeDates,
    subregionFilter,
    setSubregionFilter,
    methodFilter,
    setMethodFilter,
    isDeclineModalOpen,
    setIsDeclineModalOpen,
    isApproveModalOpen,
    setIsApproveModalOpen,
    isRefundModalOpen,
    setIsRefundModalOpen,
    isDetailsModalOpen,
    setIsDetailsModalOpen,
    isPaymentDetailsOpen,
    selectedPaymentRecord,
    closePaymentDetails,
    handleDownloadCsv,
    isDownloading,
  } = useWithdrawalReview();

  // draftFromExternalRef prevents race between debounced values and programmatic context updates by tracking whether current draft comes from user typing vs external sync.
  const [subregionDraft, setSubregionDraft] = useState(subregionFilter ?? "");
  const draftFromExternalRef = useRef(true);
  useEffect(() => {
    setSubregionDraft(subregionFilter ?? "");
    draftFromExternalRef.current = true;
  }, [subregionFilter]);

  const { effectiveSearchQuery: debouncedSubregion } = useDebouncedSearchQuery(
    subregionDraft,
    { delayMs: 1000, minLength: 0 },
  );

  useEffect(() => {
    if (draftFromExternalRef.current) return;
    // Wait for the debouncer to catch up to the latest keystroke.
    if (debouncedSubregion !== subregionDraft) return;
    const trimmed = debouncedSubregion.trim();
    if (trimmed !== (subregionFilter ?? "")) {
      setSubregionFilter(trimmed);
    }
  }, [debouncedSubregion, subregionDraft, subregionFilter, setSubregionFilter]);

  // Outer Overview/Rules switch is purely local — table-tab, filter, and
  // modal state continue to live in `WithdrawalReviewContext` so they
  // survive an Overview ↔ Rules round-trip. Tab visibility is gated by
  // `compliance.withdrawal_review.overview` / `.rules`.
  const tabs = useMemo(() => {
    const visible = [];
    if (canAccessWithdrawalReviewTab(adminPermissions, "overview")) {
      visible.push({ id: "overview", label: AppStrings.OVERVIEW });
    }
    if (canAccessWithdrawalReviewTab(adminPermissions, "rules")) {
      visible.push({ id: "rules", label: AppStrings.RULES });
    }
    return visible;
  }, [adminPermissions]);

  const [activeTab, setActiveTab] = useState("overview");

  // If the active tab is not in the visible list (e.g. permissions
  // exclude Overview while "overview" is selected), fall back to the
  // first permitted tab instead of rendering an empty panel.
  const safeActiveTab = useMemo(() => {
    if (tabs.some((tab) => tab.id === activeTab)) return activeTab;
    return tabs[0]?.id ?? null;
  }, [tabs, activeTab]);

  const overviewPanel = (
    <>
      <TableTabs onSelectTab={setSelectedTab} />

      {selectedTab === "PENDING" && (
        <PendingRequestsTable
          canReviewWithdrawal={canReviewWithdrawal}
          canRefundWithdrawal={canRefundWithdrawal}
          currencySymbol={currencySymbol}
        />
      )}
      {selectedTab === "IN_PROGRESS" && (
        <InProgressTable
          canReviewWithdrawal={canReviewWithdrawal}
          canRefundWithdrawal={canRefundWithdrawal}
          currencySymbol={currencySymbol}
        />
      )}
      {selectedTab === "PROCESSED" && (
        <ProcessedRequestsTable
          canReviewWithdrawal={canReviewWithdrawal}
          currencySymbol={currencySymbol}
        />
      )}
    </>
  );

  const paymentDetailsIds = toPaymentDetailsIds(selectedPaymentRecord);

  return (
    <>
      <PaymentDetailsDrawer
        open={isPaymentDetailsOpen}
        onClose={closePaymentDetails}
        userId={paymentDetailsIds.userId}
        transactionId={paymentDetailsIds.transactionId}
        currencySymbol={currencySymbol}
      />
      <div className={`${styles.container} withdrawal-review`}>
        <div className={styles.header}>
          {safeActiveTab === "overview" && (
            <div className={styles.headerOptions}>
              <div className={styles.subregionFilter}>
                <Input
                  data-testid="subregion-filter"
                  placeholder={AppStrings.STATE}
                  value={subregionDraft}
                  onChange={(e) => {
                    draftFromExternalRef.current = false;
                    setSubregionDraft(e.target.value);
                  }}
                  allowClear
                />
              </div>
              <div aria-hidden="true" className={styles.headerBreak} />
              <div className={styles.subregionFilter}>
                <CustomSelect
                  data-testid="method-filter"
                  value={methodFilter || undefined}
                  onChange={(next) => setMethodFilter(next ?? "")}
                  options={METHOD_OPTIONS}
                  placeholder={AppStrings.METHOD}
                  allowClear
                />
              </div>
              <CustomDatePicker
                getData={() => { }}
                dates={rangeDates}
                setDates={setRangeDates}
                showQuickSelect={true}
                showPresets={true}
              />
              <div className={styles.exportButton}>
                <button
                  type="button"
                  aria-label="Download"
                  className={`export-to-xlsx-button ${isDownloading ? "disabled" : ""}`}
                  disabled={isDownloading}
                  onClick={handleDownloadCsv}
                >
                  {isDownloading ? (
                    <LoadingOutlined className="download-icon" />
                  ) : (
                    <DownloadOutlined className="download-icon" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {tabs.length > 0 && (
          <div
            className={styles.tabs}
            role="tablist"
            aria-label={AppStrings.WITHDRAWAL_REVIEW_TITLE}
            data-testid="withdrawal-review-tabs"
          >
            {tabs.map((tab) => {
              const isActive = safeActiveTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  id={`withdrawal-review-tab-${tab.id}`}
                  aria-selected={isActive}
                  aria-controls={`withdrawal-review-panel-${tab.id}`}
                  tabIndex={isActive ? 0 : -1}
                  className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {safeActiveTab && (
          <section
            className={styles.tabPanel}
            role="tabpanel"
            id={`withdrawal-review-panel-${safeActiveTab}`}
            aria-labelledby={`withdrawal-review-tab-${safeActiveTab}`}
            data-testid={`withdrawal-review-panel-${safeActiveTab}`}
          >
            {safeActiveTab === "overview" && overviewPanel}
            {safeActiveTab === "rules" && <RulesTab />}
          </section>
        )}
      </div>

      <Modal
        closable={false}
        open={isDeclineModalOpen}
        onCancel={() => setIsDeclineModalOpen(false)}
        footer={null}
        width={400}
      >
        <DeclineRequestModal />
      </Modal>

      <Modal
        closable={false}
        open={isApproveModalOpen}
        onCancel={() => setIsApproveModalOpen(false)}
        footer={null}
        width={400}
      >
        <ApproveRequestModal />
      </Modal>

      <Modal
        closable={false}
        open={isRefundModalOpen}
        onCancel={() => setIsRefundModalOpen(false)}
        footer={null}
        width={520}
      >
        <RefundRequestModal currencySymbol={currencySymbol} />
      </Modal>

      <Modal
        closable={false}
        open={isDetailsModalOpen}
        onCancel={() => setIsDetailsModalOpen(false)}
        footer={null}
        width={450}
        className={styles.detailsModal}
      >
        <ViewDetailsModal
          canRefundWithdrawal={canRefundWithdrawal}
          currencySymbol={currencySymbol}
        />
      </Modal>
    </>
  );
}

export default function WithdrawalReview() {
  return (
    <WithdrawalReviewProvider>
      <WithdrawalReviewContent />
    </WithdrawalReviewProvider>
  );
}
